import {
    Fragment,
    useEffect,
    useState
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { CheckCircleIcon, CircleIcon } from "@primer/octicons-react";
import {
    ClockIcon,
    CheckIcon,
    PlusIcon
} from "@heroicons/react/24/outline";

import {
    IEndpointSlim,
    IOnboardingBuildParams,
    IOnboardingMilestoneStatus,
    IOnboardingOnboardingParams,
    IOnboardingOperationExpectations,
    IOnboardingOperationParams,
    IOnboardingStep,
    IOrgUser,
    ITemplateOnboarding
} from "../lib/backend/extractions.types.generated";
import { selectMemberships, selectUser, startCelebration } from "../lib/scraper.slice";
import {
    Backend,
    BackendObj
} from "../lib/backend";
import {
    classNames,
    deepCopy,
    getISODateFromDate,
    prettyDateFromISOString,
    unique
} from "../lib/utils";
import {
    TEMPLATE_ONBOARDING_STEPS,
    TIME_RANGES
} from "../lib/consts";

import { Button } from "./Button";
import { SidePanel } from "./SidePanel";
import {
    DateTextbox,
    IntegerInput,
    Textarea,
    Textbox
} from "./Textbox";
import { Dropdown } from "./Dropdown";
import { MultiselectInputField } from "./MultiselectInputField";
import { TextboxModal } from "./TextboxModal";
import { ConfirmModal } from "./ConfirmModal";
import { DropdownMenu } from "./DropdownMenu";
import { LoadingSpinner } from "./LoadingSpinner";

const INIT_BUILD_PARAMS: IOnboardingBuildParams = {
    scope_description: "",
    no_examples: 10,
    expected_weeks: 1,
    responsibilities: {
        users_uuids: [],
        approver_users_uuids: [],
        owner_uuid: "",
        csm_uuid: "",
    },
    tags: {
        green_tags: ["No Issue", "Fixed", "Will not Fix", "Out of Scope", "Cannot Reproduce"],
        red_tags: ["Does Not Work", "In Progress", "Needs Feedback"],
    },
    milestones: [
        {
            name: "Example Collection",
            description: "Sufficient number of examples collected for initial testing.",
            status: "not_started",
            due_date: "",
        },
        {
            name: "All Tests Passed",
            description: "All examples are passing with the current template version.",
            status: "not_started",
            due_date: "",
        },
        {
            name: "Integration Tested",
            description: "All integrations required for testing the template are working.",
            status: "not_started",
            due_date: "",
        }
    ],
    start_date: ""
};

const INIT_ONBOARDING_PARAMS: IOnboardingOnboardingParams = {
    scope_description: "",
    no_examples_per_user: 10,
    expected_weeks: 3,
    responsibilities: {
        users_uuids: [],
        approver_users_uuids: [],
        owner_uuid: "",
        csm_uuid: "",
    },
    tags: {
        green_tags: ["No Issue", "Fixed", "Will not Fix", "Out of Scope", "Cannot Reproduce"],
        red_tags: ["Does Not Work", "In Progress", "Needs Feedback"],
    },
    milestones: [
        {
            name: "Example Collection",
            description: "Sufficient number of examples collected for initial testing.",
            status: "not_started",
            due_date: ""
        },
        {
            name: "Tests Passed",
            description: "Required number of examples are passing with the current template version.",
            status: "not_started",
            due_date: ""
        },
        {
            name: "Integration Operational",
            description: "All integrations required for testing the template are working and operational.",
            status: "not_started",
            due_date: ""
        }
    ],
    target_green_tags_percent: 0.8,
    start_date: ""
}

const INIT_OPERATION_PARAMS: IOnboardingOperationParams = {
    expectations: [],
    responsibilities: {
        users_uuids: [],
        approver_users_uuids: [],
        owner_uuid: "",
        csm_uuid: "",
    },
    tags: {
        green_tags: ["No Issue", "Fixed", "Will not Fix", "Out of Scope", "Cannot Reproduce"],
        red_tags: ["Does Not Work", "In Progress", "Needs Feedback"],
    },
    start_date: ""
}

const INIT_OPERATION_EXPECTATION: IOnboardingOperationExpectations = {
    endpoint_uuid: "",
    type: "operation",
    expected_no_jobs: 1000,
    expected_time_saved_per_automatic_job: 10,
    expected_time_saved_per_manual_job: 3
}

function getInitBuildParams(): IOnboardingBuildParams {
    const build_params = deepCopy(INIT_BUILD_PARAMS);
    const now = new Date();
    build_params.start_date = getISODateFromDate(now);
    const due_date = new Date(now.getTime() + build_params.expected_weeks * TIME_RANGES.WEEK);
    const due_date_str = getISODateFromDate(due_date);
    for (const milestone of build_params.milestones) {
        milestone.due_date = due_date_str;
    }
    return build_params;
}

function getInitOnboardingParams(build_params: IOnboardingBuildParams): IOnboardingOnboardingParams {
    const onboarding_params = deepCopy(INIT_ONBOARDING_PARAMS);
    const now = new Date();
    onboarding_params.start_date = getISODateFromDate(now);
    const due_date = new Date(now.getTime() + onboarding_params.expected_weeks * TIME_RANGES.WEEK);
    const due_date_str = getISODateFromDate(due_date);
    for (const milestone of onboarding_params.milestones) {
        milestone.due_date = due_date_str;
    }
    onboarding_params.responsibilities = build_params.responsibilities;
    return onboarding_params;
}

function getInitOperationParams(endpoints: IEndpointSlim[], onboarding_params?: IOnboardingOnboardingParams): IOnboardingOperationParams {
    const operation_params = deepCopy(INIT_OPERATION_PARAMS) as IOnboardingOperationParams;
    for (const endpoint of endpoints) {
        operation_params.expectations.push({ ...INIT_OPERATION_EXPECTATION, endpoint_uuid: endpoint.uuid });
    }
    const now = new Date();
    operation_params.start_date = getISODateFromDate(now);
    if (onboarding_params !== undefined) {
        operation_params.responsibilities = onboarding_params.responsibilities;
    }
    return operation_params;
}

function updateExpectationsInplace(endpoints: IEndpointSlim[], operation_params?: IOnboardingOperationParams) {
    if (operation_params === undefined) { return; }
    const valid_endpoint_uuids = endpoints.map(e => e.uuid);
    // remove expectations for endpoints that no longer exist
    operation_params.expectations = operation_params.expectations.filter(e => valid_endpoint_uuids.includes(e.endpoint_uuid));
    // add expectations for new endpoints
    for (const endpoint of endpoints) {
        if (!operation_params.expectations.some(e => e.endpoint_uuid === endpoint.uuid)) {
            operation_params.expectations.push({ ...INIT_OPERATION_EXPECTATION, endpoint_uuid: endpoint.uuid });
        }
    }
    return operation_params;
}

type TemplateOnboardingProps = {
    org_uuid: string;
    template_uuid: string;
    endpoints: IEndpointSlim[];
    onboarding_progress?: number;
    operation_progress?: { jobs_count: number, expected_jobs_count: number };
    setLatestOnboarding: (onboarding: ITemplateOnboarding | undefined) => void;
}

export function TemplateOnboarding(props: TemplateOnboardingProps) {
    const { org_uuid, template_uuid, endpoints, onboarding_progress, operation_progress, setLatestOnboarding } = props;

    const dispatch = useDispatch();
    const memberships = useSelector(selectMemberships);
    const is_org_admin = memberships.some(m => m.org.uuid === org_uuid && m.role === "admin");

    const [is_loading, setIsLoading] = useState(true);
    const [is_initiating, setIsInitiating] = useState(false);
    const [is_saving, setIsSaving] = useState(false);
    const [template_onboarding, setTemplateOnboarding] = useState<ITemplateOnboarding | undefined>(undefined);
    const [show_init_step, setShowInitStep] = useState(false);
    const [show_step, setShowStep] = useState<IOnboardingStep | undefined>(undefined);

    useEffect(() => {
        BackendObj.extractions.getTemplateOnboarding({ template_uuid })
            .then(({ onboarding }) => {
                // bring operations step up-to-speed with endpoints
                updateExpectationsInplace(endpoints, onboarding?.operation);
                // store onboarding and report upstream
                setTemplateOnboarding(onboarding);
                setLatestOnboarding(onboarding);
                setIsLoading(false);
            })
            .catch(err => console.error(err));
    }, [template_uuid, endpoints, setLatestOnboarding]);

    // BUILD STEP

    const handleInitiateBuild = async (build: IOnboardingBuildParams) => {
        setIsInitiating(true);
        try {
            await BackendObj.extractions.createTemplateOnboarding({ template_uuid, build });
            const { onboarding } = await BackendObj.extractions.getTemplateOnboarding({ template_uuid });
            setTemplateOnboarding(onboarding);
            setLatestOnboarding(onboarding);
            setShowInitStep(false);
        } catch (err) {
            console.error(err);
        } finally {
            setIsInitiating(false);
        }
    }

    const handleSaveBuild = async (build: IOnboardingBuildParams) => {
        if (is_saving) { return; }
        setIsSaving(true);
        try {
            await BackendObj.extractions.updateTemplateOnboarding({ template_uuid, build });
            const { onboarding } = await BackendObj.extractions.getTemplateOnboarding({ template_uuid });
            // bring operations step up-to-speed with endpoints
            setTemplateOnboarding(onboarding);
            setLatestOnboarding(onboarding);
            setShowStep(undefined);
        } catch (err) {
            console.error(err);
        } finally {
            setIsSaving(false);
        }
    }

    const handleCompleteBuild = async (build_params?: IOnboardingBuildParams) => {
        if (is_saving) { return; }
        setIsSaving(true);
        try {
            if (build_params !== undefined) {
                await BackendObj.extractions.updateTemplateOnboarding({ template_uuid, build: build_params });
            }
            await BackendObj.extractions.setTemplateOnboardingStep({ template_uuid, step: "onboarding" });
            const { onboarding } = await BackendObj.extractions.getTemplateOnboarding({ template_uuid });
            setTemplateOnboarding(onboarding);
            setLatestOnboarding(onboarding);
            setShowStep("onboarding");
            dispatch(startCelebration());
        } catch (err) {
            console.error(err);
        } finally {
            setIsSaving(false);
        }
    }

    // ONBOARDING STEP

    const handleSaveOnboarding = async (onboarding_params: IOnboardingOnboardingParams) => {
        if (is_saving) { return; }
        setIsSaving(true);
        try {
            await BackendObj.extractions.updateTemplateOnboarding({ template_uuid, onboarding: onboarding_params });
            const { onboarding } = await BackendObj.extractions.getTemplateOnboarding({ template_uuid });
            setTemplateOnboarding(onboarding);
            setLatestOnboarding(onboarding);
            setShowStep(undefined);
        } catch (err) {
            console.error(err);
        } finally {
            setIsSaving(false);
        }
    }

    const handleOnboardingBack = async () => {
        if (is_saving) { return; }
        setIsSaving(true);
        try {
            await BackendObj.extractions.setTemplateOnboardingStep({ template_uuid, step: "build" });
            const { onboarding } = await BackendObj.extractions.getTemplateOnboarding({ template_uuid });
            setTemplateOnboarding(onboarding);
            setLatestOnboarding(onboarding);
            setShowStep(undefined);
        } catch (err) {
            console.error(err);
        } finally {
            setIsSaving(false);
        }
    }

    const handleCompleteOnboarding = async (onboarding_params?: IOnboardingOnboardingParams) => {
        if (is_saving) { return; }
        setIsSaving(true);
        try {
            if (onboarding_params !== undefined) {
                await BackendObj.extractions.updateTemplateOnboarding({ template_uuid, onboarding: onboarding_params });
            }
            await BackendObj.extractions.setTemplateOnboardingStep({ template_uuid, step: "operation" });
            const { onboarding } = await BackendObj.extractions.getTemplateOnboarding({ template_uuid });
            setTemplateOnboarding(onboarding);
            setLatestOnboarding(onboarding);
            setShowStep("operation");
            dispatch(startCelebration());
        } catch (err) {
            console.error(err);
        } finally {
            setIsSaving(false);
        }
    }

    // OPERATION STEP

    const handleSaveOperation = async (operation_params: IOnboardingOperationParams) => {
        if (is_saving) { return; }
        setIsSaving(true);
        try {
            await BackendObj.extractions.updateTemplateOnboarding({ template_uuid, operation: operation_params });
            const { onboarding } = await BackendObj.extractions.getTemplateOnboarding({ template_uuid });
            setTemplateOnboarding(onboarding);
            setLatestOnboarding(onboarding);
            setShowStep(undefined);
        } catch (err) {
            console.error(err);
        } finally {
            setIsSaving(false);
        }
    }

    const handleOperationBack = async () => {
        if (is_saving) { return; }
        setIsSaving(true);
        try {
            await BackendObj.extractions.setTemplateOnboardingStep({ template_uuid, step: "onboarding" });
            const { onboarding } = await BackendObj.extractions.getTemplateOnboarding({ template_uuid });
            setTemplateOnboarding(onboarding);
            setLatestOnboarding(onboarding);
            setShowStep(undefined);
        } catch (err) {
            console.error(err);
        } finally {
            setIsSaving(false);
        }
    }

    const handleStepClick = (step: IOnboardingStep) => {
        setShowStep(step);
    }

    if (is_loading) { return null; }

    return template_onboarding === undefined ? <div className="text-gray-600 text-sm flex flex-col gap-y-4 pb-6">
        <div>
            Here you can test your template. Examples are added here by clicking "Report Error" on any result.
            The examples and their results will be saved together with the template and can be used to evaluate
            any future changes to the template.
        </div>
        {!is_org_admin && <div>
            You can ask your tenant administrator to initiate a structured onboarding process for your template.
        </div>}
        {is_org_admin && <div className="flex flex-row items-center gap-x-2">
            <span className="flex-1">You can initiate a structured onboarding process for the template.</span>
            <Button
                text="Initiate"
                disabled={is_initiating}
                loading={is_initiating}
                onClick={() => { setShowInitStep(true); }}
            />
        </div>}
        {show_init_step && <OnboardingBuild
            current_step={"build"}
            init_step={true}
            org_uuid={org_uuid}
            build_params={getInitBuildParams()}
            onSave={handleInitiateBuild}
            onClose={() => { setShowInitStep(false); }}
        />}
    </div> : <div className="pb-6 flex flex-col gap-y-2">
        <OnboardingSteps
            onboarding={template_onboarding}
            onboarding_progress={onboarding_progress}
            operation_progress={operation_progress}
            onClick={is_org_admin ? handleStepClick : undefined}
        />

        {onboarding_progress === 1.0 && template_onboarding.step === "build" &&
            <AnnounceStepCompleted is_processing={is_saving} onComplete={() => { handleCompleteBuild(); }} />}
        {onboarding_progress === 1.0 && template_onboarding.step === "onboarding" &&
            <AnnounceStepCompleted is_processing={is_saving} onComplete={() => { handleCompleteOnboarding(); }} />}

        {show_step === "build" && <OnboardingBuild
            current_step={template_onboarding.step}
            init_step={false}
            org_uuid={org_uuid}
            build_params={template_onboarding.build}
            disabled={!is_org_admin}
            onSave={handleSaveBuild}
            onComplete={handleCompleteBuild}
            onClose={() => { setShowStep(undefined); }}
        />}
        {show_step === "onboarding" && <OnboardingOnboarding
            current_step={template_onboarding.step}
            init_step={template_onboarding.onboarding === undefined}
            org_uuid={org_uuid}
            onboarding_params={template_onboarding.onboarding ?? getInitOnboardingParams(template_onboarding.build)}
            disabled={!is_org_admin}
            onBack={handleOnboardingBack}
            onComplete={handleCompleteOnboarding}
            onSave={handleSaveOnboarding}
            onClose={() => { setShowStep(undefined); }}
        />}
        {show_step === "operation" && <OnboardingOperation
            current_step={template_onboarding.step}
            init_step={template_onboarding.operation === undefined}
            org_uuid={org_uuid}
            endpoints={endpoints}
            operation_params={template_onboarding.operation ?? getInitOperationParams(endpoints, template_onboarding.onboarding)}
            disabled={!is_org_admin}
            onBack={handleOperationBack}
            onSave={handleSaveOperation}
            onClose={() => { setShowStep(undefined); }}
        />}
    </div>;
}

type OnboardingStepProps = {
    onboarding: ITemplateOnboarding;
    onboarding_progress?: number;
    operation_progress?: { jobs_count: number, expected_jobs_count: number };
    onClick?: (step: IOnboardingStep) => void;
}

function OnboardingSteps(props: OnboardingStepProps) {
    const { onboarding, onboarding_progress, operation_progress, onClick } = props;

    const current_idx = ["build", "onboarding", "operation"].findIndex(step => onboarding.step === step);

    const getStatus = (idx: number) => {
        if (idx < current_idx) { return "complete"; }
        else if (idx === current_idx) { return "current"; }
        else { return "upcoming"; }
    }

    const steps: { name: string, step: IOnboardingStep, status: "complete" | "current" | "upcoming", progress: "bar" | "count" }[] = [
        { name: TEMPLATE_ONBOARDING_STEPS.build, step: "build", status: getStatus(0), progress: "bar" },
        { name: TEMPLATE_ONBOARDING_STEPS.onboarding, step: "onboarding", status: getStatus(1), progress: "bar" },
        { name: TEMPLATE_ONBOARDING_STEPS.operation, step: "operation", status: getStatus(2), progress: "count" },
    ];

    return <nav aria-label="Progress">
        <ol className="divide-y divide-sea_blue-600 rounded-md border border-sea_blue-600 md:flex md:divide-y-0 md:h-16">
            {steps.map(({ name, step, status, progress }, step_idx) => (
                <li key={name} className="relative md:flex md:flex-1">
                    {status === "complete" ? (
                        <div className={classNames(
                            "flex items-center px-6 py-4 text-sm font-medium bg-mint-200 w-full",
                            step_idx === 0 ? "rounded-l-md" : step_idx === steps.length - 1 ? "rounded-r-md" : "",
                            onClick ? "cursor-pointer" : ""
                        )} onClick={() => onClick?.(step)}>
                            <CheckIcon className="size-4 text-gray-900 w-5 h-5" />
                            <span className="ml-2 text-sm font-medium text-gray-900">{name}</span>
                            <span className="flex-grow flex flex-row gap-x-2 items-center justify-end lg:justify-center text-xs font-medium text-gray-500 group-hover:text-sea_blue-900">
                                {onboarding_progress !== undefined && <ProgressBar value={1} color="green" />}
                                Completed
                            </span>
                        </div>
                    ) : status === "current" ? (
                        <div className={classNames(
                            "group flex items-center px-6 py-4 text-sm font-medium bg-sea_blue-200 w-full",
                            step_idx === 0 ? "rounded-l-md" : step_idx === steps.length - 1 ? "rounded-r-md" : "",
                            onClick ? "cursor-pointer" : ""
                        )} onClick={() => onClick?.(step)}>
                            <CircleIcon className="size-4 text-sea_blue-600 w-4 h-4 group-hover:text-sea_blue-900" />
                            <span className="ml-2 text-sm font-medium text-sea_blue-600 group-hover:text-sea_blue-900">{name}</span>
                            <span className="flex-grow flex flex-row gap-x-2 items-center justify-end lg:justify-center text-xs font-medium text-gray-500 group-hover:text-sea_blue-900">
                                {progress === "bar" && onboarding_progress !== undefined && <ProgressBar value={onboarding_progress} color={onboarding_progress < 1.0 ? "blue" : "green"} />}
                                {progress === "bar" && onboarding_progress !== undefined && `${Math.round(100 * onboarding_progress)}%`}
                                {progress === "bar" && onboarding_progress === undefined && "Not Initialized"}
                                {progress === "count" && operation_progress !== undefined && operation_progress.expected_jobs_count > 0 && `${operation_progress.jobs_count} ${operation_progress.jobs_count === 1 ? "job" : "jobs"} (${Math.round(100.0 * operation_progress.jobs_count / operation_progress.expected_jobs_count)}%)`}
                                {progress === "count" && operation_progress !== undefined && operation_progress.expected_jobs_count === 0 && `${operation_progress.jobs_count} ${operation_progress.jobs_count === 1 ? "job" : "jobs"}`}
                                {progress === "count" && operation_progress === undefined && "Loading..."}
                            </span>
                        </div>
                    ) : (
                        <div className={classNames("flex items-center px-6 py-4 text-sm font-medium bg-gray-200 w-full", step_idx === 0 ? "rounded-l-md" : step_idx === steps.length - 1 ? "rounded-r-md" : "")}>
                            <ClockIcon className="size-4 text-gray-400 w-5 h-5" />
                            <span className="ml-2 text-sm font-medium text-gray-400">{name}</span>
                        </div>
                    )}

                    {step_idx !== steps.length - 1 ? <div aria-hidden="true" className="absolute right-0 top-0 hidden overflow-hidden h-16 w-5 md:block">
                        <svg fill="none" viewBox="0 0 25.8 80" preserveAspectRatio="none" className="size-full">
                            {/* Left triangle */}
                            <path
                                d="M0 -2L20 40L0 82Z"
                                stroke={step_idx < current_idx ? "#a0fadb" : step_idx === current_idx ? "#b5ddff" : "#e5e7eb"}
                                fill={step_idx < current_idx ? "#a0fadb" : step_idx === current_idx ? "#b5ddff" : "#e5e7eb"}
                            />
                            {/* Right triangles */}
                            <path
                                d="M20 40L0 -2L25.5 -2L25.5 40Z"
                                stroke={step_idx < current_idx - 1 ? "#a0fadb" : step_idx < current_idx ? "#b5ddff" : "#e5e7eb"}
                                fill={step_idx < current_idx - 1 ? "#a0fadb" : step_idx < current_idx ? "#b5ddff" : "#e5e7eb"}
                            />
                            <path
                                d="M20 40L0 82L25.5 82L25.5 40Z"
                                stroke={step_idx < current_idx - 1 ? "#a0fadb" : step_idx < current_idx ? "#b5ddff" : "#e5e7eb"}
                                fill={step_idx < current_idx - 1 ? "#a0fadb" : step_idx < current_idx ? "#b5ddff" : "#e5e7eb"}
                            />
                            {/* Stroke line on top */}
                            <path
                                d="M0 -2L20 40L0 82"
                                stroke="#0662ff"
                                vectorEffect="non-scaling-stroke"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div> : null}
                </li>
            ))}
        </ol>
    </nav>;
}

function ProgressBar({ value, color }: { value: number, color: "blue" | "green" }) {
    return <div className="w-24 md:w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className={classNames("h-full transition-all duration-300", color === "blue" ? "bg-sea_blue-500" : "bg-mint-500")}
            style={{ width: `${Math.round(value * 100)}%` }}
        />
    </div>;
}

type OnboardingBuildProps = {
    current_step: IOnboardingStep;
    init_step: boolean;
    org_uuid: string;
    build_params: IOnboardingBuildParams;
    disabled?: boolean;
    onSave: (new_build_params: IOnboardingBuildParams) => void;
    onComplete?: (new_build_params: IOnboardingBuildParams) => void;
    onClose: () => void;
}

function OnboardingBuild(props: OnboardingBuildProps) {
    const { init_step, current_step, org_uuid, build_params: initial_build_params, disabled, onSave, onComplete, onClose } = props;

    const [build_params, setBuildParams] = useState<IOnboardingBuildParams>(initial_build_params);
    const [milestones_due_date_valid, setMilestonesDueDateValid] = useState<boolean[]>(build_params.milestones.map(() => true));
    const [org_users, setOrgUsers] = useState<IOrgUser[] | undefined>(undefined);
    const [open_tags_dialog, setOpenTagsDialog] = useState<"green" | "red" | undefined>(undefined);
    const [open_tag_remove_dialog, setOpenTagRemoveDialog] = useState<{ color: "green" | "red", idx: number } | undefined>(undefined);
    const [open_cancel_dialog, setOpenCancelDialog] = useState(false);
    const [has_changes, setHasChanges] = useState(false);
    const [is_saving, setIsSaving] = useState(false);

    useEffect(() => {
        Backend.getOrgUsers({ org_uuid })
            .then((users) => { setOrgUsers(users); })
            .catch(err => console.error(err));
    }, [org_uuid]);

    const add_responsibilities = (type: "users" | "approvers", value: string) => {
        setHasChanges(true);
        const new_responsibilities = { ...build_params.responsibilities };
        if (type === "users") {
            new_responsibilities.users_uuids.push(value);
        } else {
            new_responsibilities.approver_users_uuids.push(value);
        }
        setBuildParams({ ...build_params, responsibilities: new_responsibilities });
    }

    const edit_responsibilities = (type: "users" | "approvers", idx: number, value: string) => {
        setHasChanges(true);
        const new_responsibilities = { ...build_params.responsibilities };
        if (type === "users") {
            if (value === new_responsibilities.users_uuids[idx]) {
                // same value as before, do nothing
                return;
            } else if (value === "" || new_responsibilities.users_uuids.includes(value)) {
                // remove value from list if it is already in the list or if it is an empty string
                new_responsibilities.users_uuids = new_responsibilities.users_uuids.filter((_, i) => i !== idx);
            } else {
                // update value
                new_responsibilities.users_uuids[idx] = value;
            }
        } else {
            if (value === new_responsibilities.approver_users_uuids[idx]) {
                // same value as before, do nothing
                return;
            } else if (value === "" || new_responsibilities.approver_users_uuids.includes(value)) {
                // remove value from list if it is already in the list or if it is an empty string
                new_responsibilities.approver_users_uuids = new_responsibilities.approver_users_uuids.filter((_, i) => i !== idx);
            } else {
                // update value
                new_responsibilities.approver_users_uuids[idx] = value;
            }
        }
        setBuildParams({ ...build_params, responsibilities: new_responsibilities });
    }

    const handleTagsDialogClose = (result: boolean, value?: string) => {
        setHasChanges(true);
        if (result && value !== undefined) {
            if (open_tags_dialog === "green") {
                const green_tags = unique([...build_params.tags.green_tags, value]).sort();
                const red_tags = build_params.tags.red_tags.filter(tag => tag !== value);
                setBuildParams({ ...build_params, tags: { ...build_params.tags, green_tags, red_tags } });
            } else {
                const red_tags = unique([...build_params.tags.red_tags, value]).sort();
                const green_tags = build_params.tags.green_tags.filter(tag => tag !== value);
                setBuildParams({ ...build_params, tags: { ...build_params.tags, red_tags, green_tags } });
            }
        }
        setOpenTagsDialog(undefined);
    }

    const handleTagRemove = (result: boolean) => {
        setHasChanges(true);
        if (result && open_tag_remove_dialog !== undefined) {
            const { color, idx } = open_tag_remove_dialog;
            if (color === "green") {
                const new_green_tags = build_params.tags.green_tags.filter((_, i) => i !== idx);
                setBuildParams({ ...build_params, tags: { ...build_params.tags, green_tags: new_green_tags } });
            } else {
                const new_red_tags = build_params.tags.red_tags.filter((_, i) => i !== idx);
                setBuildParams({ ...build_params, tags: { ...build_params.tags, red_tags: new_red_tags } });
            }
        }
        setOpenTagRemoveDialog(undefined);
    }

    const getMilestonePrettyStatus = (idx: number) => {
        const milestone = build_params.milestones[idx];
        if (milestone.status === "not_started") {
            return "Not Started";
        } else if (milestone.status === "in_progress") {
            return "In Progress";
        } else if (milestone.status === "completed") {
            return "Completed";
        }
        return "Unknown";
    }

    const setMilestoneStatus = (idx: number, status: IOnboardingMilestoneStatus) => {
        setHasChanges(true);
        setBuildParams({ ...build_params, milestones: [...build_params.milestones.map((m, i) => i === idx ? { ...m, status } : m)] });
    }

    const setMilestoneDescription = (idx: number, description: string) => {
        setHasChanges(true);
        setBuildParams({ ...build_params, milestones: [...build_params.milestones.map((m, i) => i === idx ? { ...m, description } : m)] });
    }

    const setMilestoneDueDate = (idx: number, due_date: Date) => {
        setHasChanges(true);
        setBuildParams({ ...build_params, milestones: [...build_params.milestones.map((m, i) => i === idx ? { ...m, due_date: getISODateFromDate(due_date) } : m)] });
    }

    const setMilestoneDueDateValid = (idx: number, valid: boolean) => {
        setMilestonesDueDateValid([...milestones_due_date_valid.slice(0, idx), valid, ...milestones_due_date_valid.slice(idx + 1)]);
    }

    const handleCancelDialogClose = (result: boolean) => {
        if (result) {
            onClose();
        }
        setOpenCancelDialog(false);
    }

    // shortcuts
    const { responsibilities, tags, milestones } = build_params;
    const { users_uuids, approver_users_uuids } = responsibilities;
    const { green_tags, red_tags } = tags;

    const possible_users = (org_users ?? []).filter(user => !users_uuids.includes(user.uuid));
    const possible_approvers = (org_users ?? []).filter(user => !approver_users_uuids.includes(user.uuid));
    const all_milestones_due_date_valid = milestones_due_date_valid.every(valid => valid);

    return <SidePanel
        title={init_step ? "Initiate Onboarding Process" : (disabled ? "View Build Step" : "Edit Build Step")}
        subtitle={"started on " + prettyDateFromISOString(build_params.start_date)}
        size="2xl"
        open={true}
        onClose={() => {
            if (is_saving) { return; }
            if (has_changes) { setOpenCancelDialog(true); }
            else { onClose(); }
        }}
    >
        {org_users === undefined ?
            <LoadingSpinner /> :
            <div>
                <div className="flex flex-col gap-y-8">
                    <div className="flex flex-col gap-y-2">
                        <div className="text-sm font-semibold">Scope</div>
                        <Textarea
                            value={build_params.scope_description}
                            onChange={value => { setHasChanges(true); setBuildParams({ ...build_params, scope_description: value }); }}
                            disabled={disabled}
                        />
                    </div>
                    <div className="grid grid-cols-3 gap-x-4">
                        <div className="col-span-2 flex flex-col gap-y-2">
                            <label htmlFor="no_examples" className="text-sm font-semibold">
                                Number of Examples
                            </label>
                            <div className="text-xs text-gray-500">
                                Minimal number of examples required to build the template and complete initial testing.
                            </div>
                        </div>
                        <div className="col-span-1">
                            <IntegerInput
                                value={build_params.no_examples}
                                disabled={disabled}
                                onChange={value => { if (value > 0) { setHasChanges(true); setBuildParams({ ...build_params, no_examples: value }); } }}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-x-4">
                        <div className="col-span-2 flex flex-col gap-y-2">
                            <label htmlFor="expected_weeks" className="text-sm font-semibold">
                                Expected Weeks
                            </label>
                            <div className="text-xs text-gray-500">
                                The number of weeks we expect it will take to build the template.
                            </div>
                        </div>
                        <div className="col-span-1">
                            <IntegerInput
                                value={build_params.expected_weeks}
                                disabled={disabled}
                                onChange={value => { if (value > 0) { setHasChanges(true); setBuildParams({ ...build_params, expected_weeks: value }); } }}
                            />
                        </div>
                    </div>
                    <div className="flex flex-col gap-y-4">
                        <div className="text-sm font-semibold">Responsibilities</div>
                        <div className="grid grid-cols-3 gap-x-4">
                            <div className="text-sm pt-2">
                                Testing Users
                            </div>
                            <div className="col-span-2 flex flex-col gap-y-2">
                                {users_uuids.map((user_uuid, idx) => <Dropdown
                                    key={idx}
                                    values={["/", ...org_users.map(user => `${user.first_name} ${user.last_name} (${user.email})`)]}
                                    ids={["", ...org_users.map(user => user.uuid)]}
                                    selected={user_uuid}
                                    disabled={disabled}
                                    onChange={value => edit_responsibilities("users", idx, value)}
                                />)}
                                {!disabled && <Dropdown
                                    values={["/", ...possible_users.map(user => `${user.first_name} ${user.last_name} (${user.email})`)]}
                                    ids={["", ...possible_users.map(user => user.uuid)]}
                                    selected={""}
                                    onChange={value => add_responsibilities("users", value)}
                                />}
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-x-4">
                            <div className="text-sm pt-2">
                                Milestone Approvers
                            </div>
                            <div className="col-span-2 flex flex-col gap-y-2">
                                {approver_users_uuids.map((user_uuid, idx) => <Dropdown
                                    key={idx}
                                    values={["/", ...org_users.map(user => `${user.first_name} ${user.last_name} (${user.email})`)]}
                                    ids={["", ...org_users.map(user => user.uuid)]}
                                    selected={user_uuid}
                                    disabled={disabled}
                                    onChange={value => edit_responsibilities("approvers", idx, value)}
                                />)}
                                {!disabled && <Dropdown
                                    values={["/", ...possible_approvers.map(user => `${user.first_name} ${user.last_name} (${user.email})`)]}
                                    ids={["", ...possible_approvers.map(user => user.uuid)]}
                                    selected={""}
                                    onChange={value => add_responsibilities("approvers", value)}
                                />}
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-x-4">
                            <div className="text-sm pt-2">
                                Process Owner
                            </div>
                            <div className="col-span-2">
                                <Dropdown
                                    values={["/", ...org_users.map(user => `${user.first_name} ${user.last_name} (${user.email})`)]}
                                    ids={["", ...org_users.map(user => user.uuid)]}
                                    selected={build_params.responsibilities.owner_uuid}
                                    disabled={disabled}
                                    onChange={value => { setHasChanges(true); setBuildParams({ ...build_params, responsibilities: { ...build_params.responsibilities, owner_uuid: value } }); }}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-3 items-center gap-x-4">
                            <div className="text-sm">
                                Customer Success Manager
                            </div>
                            <div className="col-span-2">
                                <Textbox
                                    value={build_params.responsibilities.csm_uuid}
                                    placeholder="Value provided by customer success team"
                                    disabled={disabled}
                                    onChange={value => { setHasChanges(true); setBuildParams({ ...build_params, responsibilities: { ...build_params.responsibilities, csm_uuid: value } }); }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col gap-y-4">
                        <div className="text-sm font-semibold">Tags</div>
                        <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                            <div className="text-sm pt-2">
                                Green
                            </div>
                            <div className="col-span-2 flex flex-row items-center gap-x-2">
                                <div className="grow">
                                    <MultiselectInputField
                                        values={green_tags}
                                        placeholder="Add tag"
                                        separator="space"
                                        color="green"
                                        disabled={disabled}
                                        onAdd={() => setOpenTagsDialog("green")}
                                        onRemove={(idx) => setOpenTagRemoveDialog({ color: "green", idx })}
                                    />
                                </div>
                                <Button
                                    icon={PlusIcon}
                                    disabled={disabled}
                                    onClick={() => setOpenTagsDialog("green")}
                                />
                            </div>
                            <div className="text-sm pt-2">
                                Red
                            </div>
                            <div className="col-span-2 flex flex-row items-center gap-x-2">
                                <div className="grow">
                                    <MultiselectInputField
                                        values={red_tags}
                                        placeholder="Add tag"
                                        separator="space"
                                        color="red"
                                        disabled={disabled}
                                        onAdd={() => setOpenTagsDialog("red")}
                                        onRemove={(idx) => setOpenTagRemoveDialog({ color: "red", idx })}
                                    />
                                </div>
                                <Button
                                    icon={PlusIcon}
                                    disabled={disabled}
                                    onClick={() => setOpenTagsDialog("red")}
                                />
                            </div>
                            <TextboxModal
                                open={open_tags_dialog !== undefined}
                                title={open_tags_dialog === "green" ? "Add Green Tag" : "Add Red Tag"}
                                init_text={""}
                                cancel="Cancel"
                                confirm={open_tags_dialog === "green" ? "Add" : "Add"}
                                validate={(text) => text.length > 0}
                                onClose={handleTagsDialogClose} />
                            <ConfirmModal
                                open={open_tag_remove_dialog !== undefined}
                                title={open_tag_remove_dialog?.color === "green" ? "Remove Green Tag" : "Remove Red Tag"}
                                message={["Are you sure you want to remove this tag?"]}
                                cancel="Cancel"
                                confirm="Remove"
                                onClose={handleTagRemove} />
                        </div>
                    </div>
                    <div className="flex flex-col gap-y-6">
                        <div className="text-sm font-semibold">Milestones</div>
                        {milestones.map((milestone, idx) => <div key={idx} className="flex flex-col gap-y-2">
                            <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                                <div className="col-span-2 text-sm pt-2">
                                    {`${idx + 1}. ${milestone.name}`}
                                </div>
                                <div className="text-right">
                                    <DropdownMenu
                                        title={getMilestonePrettyStatus(idx)}
                                        disabled={disabled}
                                        items={[
                                            { title: "Not Started", onClick: () => setMilestoneStatus(idx, "not_started") },
                                            { title: "In Progress", onClick: () => setMilestoneStatus(idx, "in_progress") },
                                            { title: "Completed", onClick: () => setMilestoneStatus(idx, "completed") },
                                        ]}
                                    />
                                </div>
                            </div>
                            <div className="text-xs">
                                <Textarea
                                    value={milestone.description}
                                    rows={2}
                                    disabled={disabled}
                                    onChange={value => setMilestoneDescription(idx, value)}
                                />
                            </div>
                            <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                                <div className="col-span-2 text-sm pt-2">
                                    Due Date
                                </div>
                                <div className="text-right">
                                    <DateTextbox
                                        date={new Date(milestone.due_date)}
                                        disabled={disabled}
                                        onChange={value => setMilestoneDueDate(idx, value)}
                                        reportIsValid={valid => setMilestoneDueDateValid(idx, valid)}
                                    />
                                </div>
                            </div>

                        </div>)}
                    </div>
                </div>
                <div className="flex flex-row items-center justify-end gap-x-2 pb-6 mt-6 pt-6 border-t border-gray-200">
                    {!init_step && !disabled && (current_step === "build") && onComplete !== undefined && <Button
                        text="Complete"
                        disabled={milestones.some(m => m.status !== "completed") || is_saving}
                        onClick={() => { setIsSaving(true); onComplete(build_params); }}
                    />}
                    <div className="w-4" />
                    {!disabled && <Button
                        text="Cancel"
                        disabled={is_saving}
                        onClick={() => { if (has_changes) { setOpenCancelDialog(true); } else { onClose(); } }}
                    />}
                    {!disabled && <Button
                        text={init_step ? "Initiate" : "Save"}
                        highlight={true}
                        disabled={is_saving || !all_milestones_due_date_valid}
                        loading={is_saving}
                        onClick={() => { setIsSaving(true); onSave(build_params); }}
                    />}
                    {disabled && <Button
                        text="Close"
                        onClick={() => { onClose(); }}
                    />}
                </div>
            </div>}

        <ConfirmModal
            open={open_cancel_dialog}
            title="Cancel"
            message={["Are you sure you want to cancel?"]}
            cancel="No"
            confirm="Yes"
            onClose={handleCancelDialogClose} />

    </SidePanel >;
}

type OnboardingOnboardingProps = {
    current_step: IOnboardingStep;
    init_step: boolean;
    org_uuid: string;
    onboarding_params: IOnboardingOnboardingParams;
    disabled?: boolean;
    onSave: (new_onboarding_params: IOnboardingOnboardingParams) => void;
    onBack: () => void;
    onComplete: (new_onboarding_params: IOnboardingOnboardingParams) => void;
    onClose: () => void;
}

function OnboardingOnboarding(props: OnboardingOnboardingProps) {
    const { current_step, org_uuid, onboarding_params: initial_onboarding_params, disabled, onSave, onBack, onComplete, onClose, init_step } = props;

    const user = useSelector(selectUser);
    const is_admin = user?.role === "admin";
    const memberships = useSelector(selectMemberships);
    const is_org_admin = memberships.some(m => m.org.uuid === org_uuid && m.role === "admin");

    const [onboarding_params, setOnboardingParams] = useState<IOnboardingOnboardingParams>(initial_onboarding_params);
    const [milestones_due_date_valid, setMilestonesDueDateValid] = useState<boolean[]>(onboarding_params.milestones.map(() => true));
    const [org_users, setOrgUsers] = useState<IOrgUser[] | undefined>(undefined);
    const [open_tags_dialog, setOpenTagsDialog] = useState<"green" | "red" | undefined>(undefined);
    const [open_tag_remove_dialog, setOpenTagRemoveDialog] = useState<{ color: "green" | "red", idx: number } | undefined>(undefined);
    const [open_cancel_dialog, setOpenCancelDialog] = useState(false);
    const [has_changes, setHasChanges] = useState(false);
    const [is_saving, setIsSaving] = useState(false);
    const [open_revert_dialog, setOpenRevertDialog] = useState(false);

    useEffect(() => {
        Backend.getOrgUsers({ org_uuid })
            .then((users) => { setOrgUsers(users); })
            .catch(err => console.error(err));
    }, [org_uuid]);

    const add_responsibilities = (type: "users" | "approvers", value: string) => {
        setHasChanges(true);
        const new_responsibilities = { ...onboarding_params.responsibilities };
        if (type === "users") {
            new_responsibilities.users_uuids.push(value);
        } else {
            new_responsibilities.approver_users_uuids.push(value);
        }
        setOnboardingParams({ ...onboarding_params, responsibilities: new_responsibilities });
    }

    const edit_responsibilities = (type: "users" | "approvers", idx: number, value: string) => {
        setHasChanges(true);
        const new_responsibilities = { ...onboarding_params.responsibilities };
        if (type === "users") {
            if (value === new_responsibilities.users_uuids[idx]) {
                // same value as before, do nothing
                return;
            } else if (value === "" || new_responsibilities.users_uuids.includes(value)) {
                // remove value from list if it is already in the list or if it is an empty string
                new_responsibilities.users_uuids = new_responsibilities.users_uuids.filter((_, i) => i !== idx);
            } else {
                // update value
                new_responsibilities.users_uuids[idx] = value;
            }
        } else {
            if (value === new_responsibilities.approver_users_uuids[idx]) {
                // same value as before, do nothing
                return;
            } else if (value === "" || new_responsibilities.approver_users_uuids.includes(value)) {
                // remove value from list if it is already in the list or if it is an empty string
                new_responsibilities.approver_users_uuids = new_responsibilities.approver_users_uuids.filter((_, i) => i !== idx);
            } else {
                // update value
                new_responsibilities.approver_users_uuids[idx] = value;
            }
        }
        setOnboardingParams({ ...onboarding_params, responsibilities: new_responsibilities });
    }

    const handleTagsDialogClose = (result: boolean, value?: string) => {
        setHasChanges(true);
        if (result && value !== undefined) {
            if (open_tags_dialog === "green") {
                const green_tags = unique([...onboarding_params.tags.green_tags, value]).sort();
                const red_tags = onboarding_params.tags.red_tags.filter(tag => tag !== value);
                setOnboardingParams({ ...onboarding_params, tags: { ...onboarding_params.tags, green_tags, red_tags } });
            } else {
                const red_tags = unique([...onboarding_params.tags.red_tags, value]).sort();
                const green_tags = onboarding_params.tags.green_tags.filter(tag => tag !== value);
                setOnboardingParams({ ...onboarding_params, tags: { ...onboarding_params.tags, red_tags, green_tags } });
            }
        }
        setOpenTagsDialog(undefined);
    }

    const handleTagRemove = (result: boolean) => {
        setHasChanges(true);
        if (result && open_tag_remove_dialog !== undefined) {
            const { color, idx } = open_tag_remove_dialog;
            if (color === "green") {
                const new_green_tags = onboarding_params.tags.green_tags.filter((_, i) => i !== idx);
                setOnboardingParams({ ...onboarding_params, tags: { ...onboarding_params.tags, green_tags: new_green_tags } });
            } else {
                const new_red_tags = onboarding_params.tags.red_tags.filter((_, i) => i !== idx);
                setOnboardingParams({ ...onboarding_params, tags: { ...onboarding_params.tags, red_tags: new_red_tags } });
            }
        }
        setOpenTagRemoveDialog(undefined);
    }

    const getMilestonePrettyStatus = (idx: number) => {
        const milestone = onboarding_params.milestones[idx];
        if (milestone.status === "not_started") {
            return "Not Started";
        } else if (milestone.status === "in_progress") {
            return "In Progress";
        } else if (milestone.status === "completed") {
            return "Completed";
        }
        return "Unknown";
    }

    const setMilestoneStatus = (idx: number, status: IOnboardingMilestoneStatus) => {
        setHasChanges(true);
        setOnboardingParams({ ...onboarding_params, milestones: [...onboarding_params.milestones.map((m, i) => i === idx ? { ...m, status } : m)] });
    }

    const setMilestoneDescription = (idx: number, description: string) => {
        setHasChanges(true);
        setOnboardingParams({ ...onboarding_params, milestones: [...onboarding_params.milestones.map((m, i) => i === idx ? { ...m, description } : m)] });
    }

    const setMilestoneDueDate = (idx: number, due_date: Date) => {
        setHasChanges(true);
        setOnboardingParams({ ...onboarding_params, milestones: [...onboarding_params.milestones.map((m, i) => i === idx ? { ...m, due_date: getISODateFromDate(due_date) } : m)] });
    }

    const setMilestoneDueDateValid = (idx: number, valid: boolean) => {
        setMilestonesDueDateValid([...milestones_due_date_valid.slice(0, idx), valid, ...milestones_due_date_valid.slice(idx + 1)]);
    }

    const handleCancelDialogClose = (result: boolean) => {
        if (result) {
            onClose();
        }
        setOpenCancelDialog(false);
    }

    // shortcuts
    const { responsibilities, tags, milestones } = onboarding_params;
    const { users_uuids, approver_users_uuids } = responsibilities;
    const { green_tags, red_tags } = tags;

    const possible_users = (org_users ?? []).filter(user => !users_uuids.includes(user.uuid));
    const possible_approvers = (org_users ?? []).filter(user => !approver_users_uuids.includes(user.uuid));
    const all_milestones_due_date_valid = milestones_due_date_valid.every(valid => valid);

    return <SidePanel
        title={init_step ? "Initialize Onboarding Step" : (disabled ? "View Onboarding Step" : "Edit Onboarding Step")}
        subtitle={"started on " + prettyDateFromISOString(onboarding_params.start_date)}
        size="2xl"
        open={true}
        onClose={() => {
            if (is_saving) { return; }
            if (has_changes) { setOpenCancelDialog(true); }
            else { onClose(); }
        }}
    >
        {org_users === undefined ?
            <LoadingSpinner /> :
            <div>
                <div className="flex flex-col gap-y-8">
                    <div className="flex flex-col gap-y-2">
                        <div className="text-sm font-semibold">Scope</div>
                        <Textarea
                            value={onboarding_params.scope_description}
                            onChange={value => { setHasChanges(true); setOnboardingParams({ ...onboarding_params, scope_description: value }); }}
                            disabled={disabled}
                        />
                    </div>
                    <div className="grid grid-cols-3 gap-x-4">
                        <div className="col-span-2 flex flex-col gap-y-2">
                            <label htmlFor="no_examples" className="text-sm font-semibold">
                                Number of Examples per User
                            </label>
                            <div className="text-xs text-gray-500">
                                Minimal number of examples required from each user to complete the onboarding process.
                            </div>
                        </div>
                        <div className="col-span-1">
                            <IntegerInput
                                value={onboarding_params.no_examples_per_user}
                                disabled={disabled}
                                onChange={value => { if (value > 0) { setHasChanges(true); setOnboardingParams({ ...onboarding_params, no_examples_per_user: value }); } }}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-x-4">
                        <div className="col-span-2 flex flex-col gap-y-2">
                            <label htmlFor="expected_weeks" className="text-sm font-semibold">
                                Expected Weeks
                            </label>
                            <div className="text-xs text-gray-500">
                                The number of weeks we expect it will take to complete the onboarding process.
                            </div>
                        </div>
                        <div className="col-span-1">
                            <IntegerInput
                                value={onboarding_params.expected_weeks}
                                disabled={disabled}
                                onChange={value => { if (value > 0) { setHasChanges(true); setOnboardingParams({ ...onboarding_params, expected_weeks: value }); } }}
                            />
                        </div>
                    </div>
                    <div className="flex flex-col gap-y-4">
                        <div className="text-sm font-semibold">Responsibilities</div>
                        <div className="grid grid-cols-3 gap-x-4">
                            <div className="text-sm pt-2">
                                Testing Users
                            </div>
                            <div className="col-span-2 flex flex-col gap-y-2">
                                {users_uuids.map((user_uuid, idx) => <Dropdown
                                    key={idx}
                                    values={["/", ...org_users.map(user => `${user.first_name} ${user.last_name} (${user.email})`)]}
                                    ids={["", ...org_users.map(user => user.uuid)]}
                                    selected={user_uuid}
                                    disabled={disabled}
                                    onChange={value => edit_responsibilities("users", idx, value)}
                                />)}
                                {!disabled && <Dropdown
                                    values={["/", ...possible_users.map(user => `${user.first_name} ${user.last_name} (${user.email})`)]}
                                    ids={["", ...possible_users.map(user => user.uuid)]}
                                    selected={""}
                                    onChange={value => add_responsibilities("users", value)}
                                />}
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-x-4">
                            <div className="text-sm pt-2">
                                Milestone Approvers
                            </div>
                            <div className="col-span-2 flex flex-col gap-y-2">
                                {approver_users_uuids.map((user_uuid, idx) => <Dropdown
                                    key={idx}
                                    values={["/", ...org_users.map(user => `${user.first_name} ${user.last_name} (${user.email})`)]}
                                    ids={["", ...org_users.map(user => user.uuid)]}
                                    selected={user_uuid}
                                    disabled={disabled}
                                    onChange={value => edit_responsibilities("approvers", idx, value)}
                                />)}
                                {!disabled && <Dropdown
                                    values={["/", ...possible_approvers.map(user => `${user.first_name} ${user.last_name} (${user.email})`)]}
                                    ids={["", ...possible_approvers.map(user => user.uuid)]}
                                    selected={""}
                                    onChange={value => add_responsibilities("approvers", value)}
                                />}
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-x-4">
                            <div className="text-sm pt-2">
                                Process Owner
                            </div>
                            <div className="col-span-2">
                                <Dropdown
                                    values={["/", ...org_users.map(user => `${user.first_name} ${user.last_name} (${user.email})`)]}
                                    ids={["", ...org_users.map(user => user.uuid)]}
                                    selected={onboarding_params.responsibilities.owner_uuid}
                                    disabled={disabled}
                                    onChange={value => { setHasChanges(true); setOnboardingParams({ ...onboarding_params, responsibilities: { ...onboarding_params.responsibilities, owner_uuid: value } }); }}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-3 items-center gap-x-4">
                            <div className="text-sm">
                                Customer Success Manager
                            </div>
                            <div className="col-span-2">
                                <Textbox
                                    value={onboarding_params.responsibilities.csm_uuid}
                                    placeholder="Value provided by customer success team"
                                    disabled={disabled}
                                    onChange={value => { setHasChanges(true); setOnboardingParams({ ...onboarding_params, responsibilities: { ...onboarding_params.responsibilities, csm_uuid: value } }); }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col gap-y-4">
                        <div className="text-sm font-semibold">Tags</div>
                        <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                            <div className="text-sm pt-2">
                                Green
                            </div>
                            <div className="col-span-2 flex flex-row items-center gap-x-2">
                                <div className="grow">
                                    <MultiselectInputField
                                        values={green_tags}
                                        placeholder="Add tag"
                                        separator="space"
                                        color="green"
                                        disabled={disabled}
                                        onAdd={() => setOpenTagsDialog("green")}
                                        onRemove={(idx) => setOpenTagRemoveDialog({ color: "green", idx })}
                                    />
                                </div>
                                <Button
                                    icon={PlusIcon}
                                    disabled={disabled}
                                    onClick={() => setOpenTagsDialog("green")}
                                />
                            </div>
                            <div className="text-sm pt-2">
                                Red
                            </div>
                            <div className="col-span-2 flex flex-row items-center gap-x-2">
                                <div className="grow">
                                    <MultiselectInputField
                                        values={red_tags}
                                        placeholder="Add tag"
                                        separator="space"
                                        color="red"
                                        disabled={disabled}
                                        onAdd={() => setOpenTagsDialog("red")}
                                        onRemove={(idx) => setOpenTagRemoveDialog({ color: "red", idx })}
                                    />
                                </div>
                                <Button
                                    icon={PlusIcon}
                                    disabled={disabled}
                                    onClick={() => setOpenTagsDialog("red")}
                                />
                            </div>
                            <TextboxModal
                                open={open_tags_dialog !== undefined}
                                title={open_tags_dialog === "green" ? "Add Green Tag" : "Add Red Tag"}
                                init_text={""}
                                cancel="Cancel"
                                confirm={open_tags_dialog === "green" ? "Add" : "Add"}
                                validate={(text) => text.length > 0}
                                onClose={handleTagsDialogClose} />
                            <ConfirmModal
                                open={open_tag_remove_dialog !== undefined}
                                title={open_tag_remove_dialog?.color === "green" ? "Remove Green Tag" : "Remove Red Tag"}
                                message={["Are you sure you want to remove this tag?"]}
                                cancel="Cancel"
                                confirm="Remove"
                                onClose={handleTagRemove} />
                        </div>
                    </div>
                    <div className="flex flex-col gap-y-6">
                        <div className="text-sm font-semibold">Milestones</div>
                        {milestones.map((milestone, idx) => <div key={idx} className="flex flex-col gap-y-2">
                            <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                                <div className="col-span-2 text-sm pt-2">
                                    {`${idx + 1}. ${milestone.name}`}
                                </div>
                                <div className="text-right">
                                    <DropdownMenu
                                        title={getMilestonePrettyStatus(idx)}
                                        disabled={disabled}
                                        items={[
                                            { title: "Not Started", onClick: () => setMilestoneStatus(idx, "not_started") },
                                            { title: "In Progress", onClick: () => setMilestoneStatus(idx, "in_progress") },
                                            { title: "Completed", onClick: () => setMilestoneStatus(idx, "completed") },
                                        ]}
                                    />
                                </div>
                            </div>
                            <div className="text-xs">
                                <Textarea
                                    value={milestone.description}
                                    rows={2}
                                    disabled={disabled}
                                    onChange={value => setMilestoneDescription(idx, value)}
                                />
                            </div>
                            <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                                <div className="col-span-2 text-sm pt-2">
                                    Due Date
                                </div>
                                <div className="text-right">
                                    <DateTextbox
                                        date={new Date(milestone.due_date)}
                                        disabled={disabled}
                                        onChange={value => setMilestoneDueDate(idx, value)}
                                        reportIsValid={valid => setMilestoneDueDateValid(idx, valid)}
                                    />
                                </div>
                            </div>

                        </div>)}
                    </div>
                </div>
                <div className="flex flex-row items-center justify-end gap-x-2 pb-6 mt-6 pt-6 border-t border-gray-200">
                    {!disabled && (current_step === "onboarding") && onBack !== undefined && (is_admin || is_org_admin) && <Button
                        text="Revert to Building Step"
                        disabled={is_saving}
                        onClick={() => { setOpenRevertDialog(true); }}
                    />}
                    {!disabled && (current_step === "onboarding") && onComplete !== undefined && <Button
                        text="Complete"
                        disabled={milestones.some(m => m.status !== "completed") || is_saving}
                        onClick={() => { setIsSaving(true); onComplete(onboarding_params); }}
                    />}
                    <div className="w-4" />
                    {!disabled && <Button
                        text="Cancel"
                        disabled={is_saving}
                        onClick={() => { if (has_changes) { setOpenCancelDialog(true); } else { onClose(); } }}
                    />}
                    {!disabled && <Button
                        text={init_step ? "Initialize" : "Save"}
                        highlight={true}
                        disabled={is_saving || !all_milestones_due_date_valid}
                        loading={is_saving}
                        onClick={() => { setIsSaving(true); onSave(onboarding_params); }}
                    />}
                    {disabled && <Button
                        text="Close"
                        onClick={() => { onClose(); }}
                    />}
                </div>
            </div>}

        <ConfirmModal
            open={open_cancel_dialog}
            title="Cancel"
            message={["Are you sure you want to cancel?"]}
            cancel="No"
            confirm="Yes"
            onClose={handleCancelDialogClose} />

        <ConfirmModal
            open={open_revert_dialog}
            title="Revert to Building Step"
            message={["Are you sure you want to revert from Onboarding to Building step?"]}
            cancel="No"
            confirm="Yes"
            onClose={(result) => {
                setOpenRevertDialog(false);
                if (result) { onBack(); }
            }} />

    </SidePanel >;
}

type OnboardingOperationProps = {
    current_step: IOnboardingStep;
    init_step: boolean;
    org_uuid: string;
    endpoints: IEndpointSlim[];
    operation_params: IOnboardingOperationParams;
    disabled?: boolean;
    onSave: (new_operation_params: IOnboardingOperationParams) => void;
    onBack: () => void;
    onClose: () => void;
}

function OnboardingOperation(props: OnboardingOperationProps) {
    const { current_step, org_uuid, endpoints, operation_params: initial_operation_params, disabled, onSave, onBack, onClose, init_step } = props;

    const user = useSelector(selectUser);
    const is_admin = user?.role === "admin";
    const memberships = useSelector(selectMemberships);
    const is_org_admin = memberships.some(m => m.org.uuid === org_uuid && m.role === "admin");

    const [operation_params, setOperationParams] = useState<IOnboardingOperationParams>(initial_operation_params);
    const [org_users, setOrgUsers] = useState<IOrgUser[] | undefined>(undefined);
    const [open_tags_dialog, setOpenTagsDialog] = useState<"green" | "red" | undefined>(undefined);
    const [open_tag_remove_dialog, setOpenTagRemoveDialog] = useState<{ color: "green" | "red", idx: number } | undefined>(undefined);
    const [open_cancel_dialog, setOpenCancelDialog] = useState(false);
    const [has_changes, setHasChanges] = useState(false);
    const [is_saving, setIsSaving] = useState(false);
    const [open_revert_dialog, setOpenRevertDialog] = useState(false);

    useEffect(() => {
        Backend.getOrgUsers({ org_uuid })
            .then((users) => { setOrgUsers(users); })
            .catch(err => console.error(err));
    }, [org_uuid]);

    const updateExpectation = (idx: number, expectation: IOnboardingOperationExpectations) => {
        setHasChanges(true);
        const new_expectations = [...operation_params.expectations];
        new_expectations[idx] = expectation;
        setOperationParams({ ...operation_params, expectations: new_expectations });
    }

    const getExpectationType = (expectation: IOnboardingOperationExpectations) => {
        if (expectation.type === "operation") { return "Operational"; }
        if (expectation.type === "testing") { return "Testing"; }
        if (expectation.type === "other") { return "Other"; }
        return "Unknown";
    }

    const addResponsibilities = (value: string) => {
        setHasChanges(true);
        const new_responsibilities = { ...operation_params.responsibilities };
        new_responsibilities.users_uuids.push(value);
        setOperationParams({ ...operation_params, responsibilities: new_responsibilities });
    }

    const editResponsibilities = (idx: number, value: string) => {
        setHasChanges(true);
        const new_responsibilities = { ...operation_params.responsibilities };
        if (value === new_responsibilities.users_uuids[idx]) {
            // same value as before, do nothing
            return;
        } else if (value === "" || new_responsibilities.users_uuids.includes(value)) {
            // remove value from list if it is already in the list or if it is an empty string
            new_responsibilities.users_uuids = new_responsibilities.users_uuids.filter((_, i) => i !== idx);
        } else {
            // update value
            new_responsibilities.users_uuids[idx] = value;
        }
        setOperationParams({ ...operation_params, responsibilities: new_responsibilities });
    }

    const handleTagsDialogClose = (result: boolean, value?: string) => {
        setHasChanges(true);
        if (result && value !== undefined) {
            if (open_tags_dialog === "green") {
                const green_tags = unique([...operation_params.tags.green_tags, value]).sort();
                const red_tags = operation_params.tags.red_tags.filter(tag => tag !== value);
                setOperationParams({ ...operation_params, tags: { ...operation_params.tags, green_tags, red_tags } });
            } else {
                const red_tags = unique([...operation_params.tags.red_tags, value]).sort();
                const green_tags = operation_params.tags.green_tags.filter(tag => tag !== value);
                setOperationParams({ ...operation_params, tags: { ...operation_params.tags, red_tags, green_tags } });
            }
        }
        setOpenTagsDialog(undefined);
    }

    const handleTagRemove = (result: boolean) => {
        setHasChanges(true);
        if (result && open_tag_remove_dialog !== undefined) {
            const { color, idx } = open_tag_remove_dialog;
            if (color === "green") {
                const new_green_tags = operation_params.tags.green_tags.filter((_, i) => i !== idx);
                setOperationParams({ ...operation_params, tags: { ...operation_params.tags, green_tags: new_green_tags } });
            } else {
                const new_red_tags = operation_params.tags.red_tags.filter((_, i) => i !== idx);
                setOperationParams({ ...operation_params, tags: { ...operation_params.tags, red_tags: new_red_tags } });
            }
        }
        setOpenTagRemoveDialog(undefined);
    }

    const handleCancelDialogClose = (result: boolean) => {
        if (result) {
            onClose();
        }
        setOpenCancelDialog(false);
    }

    // shortcuts
    const { responsibilities, tags } = operation_params;
    const { users_uuids } = responsibilities;
    const { green_tags, red_tags } = tags;

    const possible_users = (org_users ?? []).filter(user => !users_uuids.includes(user.uuid));

    return <SidePanel
        title={init_step ? "Initialize Onboarding Step" : (disabled ? "View Onboarding Step" : "Edit Onboarding Step")}
        subtitle={"started on " + prettyDateFromISOString(operation_params.start_date)}
        size="2xl"
        open={true}
        onClose={() => {
            if (is_saving) { return; }
            if (has_changes) { setOpenCancelDialog(true); }
            else { onClose(); }
        }}
    >
        {org_users === undefined ?
            <LoadingSpinner /> :
            <div>
                <div className="flex flex-col gap-y-8">
                    <div className="flex flex-col gap-y-4">
                        <div className="text-sm font-semibold">Expectations per Integration</div>
                        {operation_params.expectations.map((expectation, expectation_idx) => <div key={expectation_idx} className="flex flex-col gap-y-4 pb-4">
                            <div className="grid grid-cols-3 gap-x-4">
                                <div className="col-span-2 text-sm pt-2">
                                    {endpoints.find(e => e.uuid === expectation.endpoint_uuid)?.name}
                                </div>
                                <div className="text-right">
                                    <DropdownMenu
                                        title={getExpectationType(expectation)}
                                        disabled={disabled}
                                        items={[
                                            { title: "Operational", onClick: () => updateExpectation(expectation_idx, { ...expectation, type: "operation" }) },
                                            { title: "Testing", onClick: () => updateExpectation(expectation_idx, { ...expectation, type: "testing" }) },
                                            { title: "Other", onClick: () => updateExpectation(expectation_idx, { ...expectation, type: "other" }) },
                                        ]}
                                    />
                                </div>
                            </div>
                            {expectation.type === "operation" && <Fragment>
                                <div className="grid grid-cols-3 gap-x-4">
                                    <div className="col-span-2 flex flex-col gap-y-2">
                                        <label htmlFor="no_examples" className="text-sm font-semibold">
                                            Number of jobs per month
                                        </label>
                                        <div className="text-xs text-gray-500">
                                            The number of jobs we expect to be processed per month of operation.
                                        </div>
                                    </div>
                                    <div className="col-span-1">
                                        <IntegerInput
                                            value={expectation.expected_no_jobs}
                                            disabled={disabled}
                                            onChange={value => { if (value > 0) { setHasChanges(true); updateExpectation(expectation_idx, { ...expectation, expected_no_jobs: value }); } }}
                                        />
                                    </div>
                                </div>
                                {["never", "on_error"].includes(endpoints.find(e => e.uuid === expectation.endpoint_uuid)?.require_confirmation ?? "") &&
                                    <div className="grid grid-cols-3 gap-x-4">
                                        <div className="col-span-2 flex flex-col gap-y-2">
                                            <label htmlFor="expected_weeks" className="text-sm font-semibold">
                                                Time saved per job with no or automatic confirmation
                                            </label>
                                            <div className="text-xs text-gray-500">
                                                Number of minutes of manual time we expect each job to save when no manual confirmation workflow is required.
                                            </div>
                                        </div>
                                        <div className="col-span-1">
                                            <IntegerInput
                                                value={expectation.expected_time_saved_per_automatic_job}
                                                disabled={disabled}
                                                onChange={value => { if (value >= 0) { setHasChanges(true); updateExpectation(expectation_idx, { ...expectation, expected_time_saved_per_automatic_job: value }); } }}
                                            />
                                        </div>
                                    </div>}
                                {["on_error", "always"].includes(endpoints.find(e => e.uuid === expectation.endpoint_uuid)?.require_confirmation ?? "") &&
                                    <div className="grid grid-cols-3 gap-x-4">
                                        <div className="col-span-2 flex flex-col gap-y-2">
                                            <label htmlFor="expected_weeks" className="text-sm font-semibold">
                                                Time saved per job with manual confirmation
                                            </label>
                                            <div className="text-xs text-gray-500">
                                                Number of minutes of manual time we expect each job to save when a manual confirmation workflow is required to complete the job.
                                            </div>
                                        </div>
                                        <div className="col-span-1">
                                            <IntegerInput
                                                value={expectation.expected_time_saved_per_manual_job}
                                                disabled={disabled}
                                                onChange={value => { if (value >= 0) { setHasChanges(true); updateExpectation(expectation_idx, { ...expectation, expected_time_saved_per_manual_job: value }); } }}
                                            />
                                        </div>
                                    </div>}
                            </Fragment>}
                            {expectation.type !== "operation" && <div className="text-xs text-gray-500">
                                Only operational integrations are included in operational stats. Testing or other integrations are not included, to avoid skewing the results.
                            </div>}
                        </div>)}
                    </div>

                    <div className="flex flex-col gap-y-4">
                        <div className="text-sm font-semibold">Responsibilities</div>
                        <div className="grid grid-cols-3 gap-x-4">
                            <div className="text-sm pt-2">
                                Users
                            </div>
                            <div className="col-span-2 flex flex-col gap-y-2">
                                {users_uuids.map((user_uuid, idx) => <Dropdown
                                    key={idx}
                                    values={["/", ...org_users.map(user => `${user.first_name} ${user.last_name} (${user.email})`)]}
                                    ids={["", ...org_users.map(user => user.uuid)]}
                                    selected={user_uuid}
                                    disabled={disabled}
                                    onChange={value => editResponsibilities(idx, value)}
                                />)}
                                {!disabled && <Dropdown
                                    values={["/", ...possible_users.map(user => `${user.first_name} ${user.last_name} (${user.email})`)]}
                                    ids={["", ...possible_users.map(user => user.uuid)]}
                                    selected={""}
                                    onChange={value => addResponsibilities(value)}
                                />}
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-x-4">
                            <div className="text-sm pt-2">
                                Process Owner
                            </div>
                            <div className="col-span-2">
                                <Dropdown
                                    values={["/", ...org_users.map(user => `${user.first_name} ${user.last_name} (${user.email})`)]}
                                    ids={["", ...org_users.map(user => user.uuid)]}
                                    selected={operation_params.responsibilities.owner_uuid}
                                    disabled={disabled}
                                    onChange={value => { setHasChanges(true); setOperationParams({ ...operation_params, responsibilities: { ...operation_params.responsibilities, owner_uuid: value } }); }}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-3 items-center gap-x-4">
                            <div className="text-sm">
                                Customer Success Manager
                            </div>
                            <div className="col-span-2">
                                <Textbox
                                    value={operation_params.responsibilities.csm_uuid}
                                    placeholder="Value provided by customer success team"
                                    disabled={disabled}
                                    onChange={value => { setHasChanges(true); setOperationParams({ ...operation_params, responsibilities: { ...operation_params.responsibilities, csm_uuid: value } }); }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col gap-y-4">
                        <div className="text-sm font-semibold">Tags</div>
                        <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                            <div className="text-sm pt-2">
                                Green
                            </div>
                            <div className="col-span-2 flex flex-row items-center gap-x-2">
                                <div className="grow">
                                    <MultiselectInputField
                                        values={green_tags}
                                        placeholder="Add tag"
                                        separator="space"
                                        color="green"
                                        disabled={disabled}
                                        onAdd={() => setOpenTagsDialog("green")}
                                        onRemove={(idx) => setOpenTagRemoveDialog({ color: "green", idx })}
                                    />
                                </div>
                                <Button
                                    icon={PlusIcon}
                                    disabled={disabled}
                                    onClick={() => setOpenTagsDialog("green")}
                                />
                            </div>
                            <div className="text-sm pt-2">
                                Red
                            </div>
                            <div className="col-span-2 flex flex-row items-center gap-x-2">
                                <div className="grow">
                                    <MultiselectInputField
                                        values={red_tags}
                                        placeholder="Add tag"
                                        separator="space"
                                        color="red"
                                        disabled={disabled}
                                        onAdd={() => setOpenTagsDialog("red")}
                                        onRemove={(idx) => setOpenTagRemoveDialog({ color: "red", idx })}
                                    />
                                </div>
                                <Button
                                    icon={PlusIcon}
                                    disabled={disabled}
                                    onClick={() => setOpenTagsDialog("red")}
                                />
                            </div>
                            <TextboxModal
                                open={open_tags_dialog !== undefined}
                                title={open_tags_dialog === "green" ? "Add Green Tag" : "Add Red Tag"}
                                init_text={""}
                                cancel="Cancel"
                                confirm={open_tags_dialog === "green" ? "Add" : "Add"}
                                validate={(text) => text.length > 0}
                                onClose={handleTagsDialogClose} />
                            <ConfirmModal
                                open={open_tag_remove_dialog !== undefined}
                                title={open_tag_remove_dialog?.color === "green" ? "Remove Green Tag" : "Remove Red Tag"}
                                message={["Are you sure you want to remove this tag?"]}
                                cancel="Cancel"
                                confirm="Remove"
                                onClose={handleTagRemove} />
                        </div>
                    </div>
                </div>
                <div className="flex flex-row items-center justify-end gap-x-2 pb-6 mt-6 pt-6 border-t border-gray-200">
                    {!disabled && (current_step === "operation") && onBack !== undefined && (is_admin || is_org_admin) && <Button
                        text="Revert to Onboarding Step"
                        disabled={is_saving}
                        onClick={() => { setOpenRevertDialog(true); }}
                    />}
                    <div className="w-4" />
                    {!disabled && <Button
                        text="Cancel"
                        disabled={is_saving}
                        onClick={() => { if (has_changes) { setOpenCancelDialog(true); } else { onClose(); } }}
                    />}
                    {!disabled && <Button
                        text={init_step ? "Initialize" : "Save"}
                        highlight={true}
                        disabled={is_saving}
                        loading={is_saving}
                        onClick={() => { setIsSaving(true); onSave(operation_params); }}
                    />}
                    {disabled && <Button
                        text="Close"
                        onClick={() => { onClose(); }}
                    />}
                </div>
            </div>}

        <ConfirmModal
            open={open_cancel_dialog}
            title="Cancel"
            message={["Are you sure you want to cancel?"]}
            cancel="No"
            confirm="Yes"
            onClose={handleCancelDialogClose} />

        <ConfirmModal
            open={open_revert_dialog}
            title="Revert to Onboarding Step"
            message={["Are you sure you want to revert from Operation to Onboarding step?"]}
            cancel="No"
            confirm="Yes"
            onClose={(result) => {
                setOpenRevertDialog(false);
                if (result) { onBack(); }
            }} />

    </SidePanel >;
}

type AnnounceStepCompletedProps = {
    is_processing: boolean;
    onComplete: () => void;
}

function AnnounceStepCompleted(props: AnnounceStepCompletedProps) {
    const { is_processing, onComplete } = props;

    return <div className="rounded-md bg-mint-50 border border-mint-400 px-6 mt-2 py-4">
        <div className="flex">
            <div className="shrink-0">
                <CheckCircleIcon aria-hidden="true" className="size-5 text-mint-400" />
            </div>
            <div className="ml-6">
                <h3 className="text-sm font-medium text-green-800">All Milestones Completed</h3>
                <div className="mt-2 text-sm text-green-700">
                    <p>You can now move to the next lifecycle step.</p>
                </div>
            </div>
            <div className="ml-auto my-auto">
                <Button
                    text="Move to next step"
                    disabled={is_processing}
                    loading={is_processing}
                    onClick={onComplete}
                />
            </div>
        </div>
    </div>;
}
